import React, { useState, useEffect, useContext, useMemo } from 'react'
import { Container, Row, Col, Dropdown, DropdownToggle, DropdownItem, DropdownMenu } from "reactstrap"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom" // using useParams for React Router v6 compatibility
import CommsConversation from "./CommsConversation"
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore"
import { firestoreDb } from "../Components/firebase"
import { useQuery } from "@apollo/client"
import "./CommsContainer.css"
import { MyInboxesQuery } from './Queries'
import { CurrentUserContext } from '../Context/CurrentUserContext'
import moment from 'moment'
import CommsNewConversationModal from './CommsNewConversationModal'
import { AllUsersPresenceProvider } from '../Context/AllUsersPresenceProvider'
import Loader from '../Material/Loader'

// TODO:
// [ ] - Add pagination for conversations if more than X
// [x] - Add filter for assigned to me
// [ ] - Sort by assigned to me first
// [ ] - Sort by last message date
// [ ] - Assigning con agents should show agent online status from presence
// [x] - New conversation page
// [ ] - When new message is created, redirect to that conversation if not already there
// [ ] - In admin when saving new channel, if twilio, update the twilio webhook to point to our messaging webhook with twilio api
// [ ] - Move unread sidbar count to context and update globally (with firebase listener?)

const COMMS_CONVERSATIONS_COLLECTION_NAME = "messaging_conversation"

export default function CommsContainer({ ...props }) {
    const navigate = useNavigate()
    const { currentUser } = useContext(CurrentUserContext)

    const { data, loading: loadingInboxes, error: errorInboxes, refetch, variables } = useQuery(
        MyInboxesQuery,
        {
            fetchPolicy: 'network-only',
        }
    )
    // All inboxes the user has access to
    const allInboxes = data?.myInboxes?.edges?.map(edge => edge.node) || []
    const allInboxesIds = allInboxes?.map(inbox => inbox.id) || [] // Inbox graphene IDs
    const allInboxesIdsKey = useMemo(() => allInboxesIds.join(","), [allInboxesIds]);

    const { conversationId } = useParams()
    const [conversations, setConversations] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [searchParams, setSearchParams] = useSearchParams()
    const [inboxOpen, setInboxOpen] = useState(false);
    const [statusOpen, setStatusOpen] = useState(false);
    const [assignmentOpen, setAssignmentOpen] = useState(false);
    const [channelOpen, setChannelOpen] = useState(false);
    const STATUSES = ["All", "Open", "Archived"]
    const ASSIGNMENT_OPTIONS = ["Unassigned", "Assigned To Me", "Not Assigned To Me", "All"]
    const CHANNEL_TYPE_LABELS = {
        "email": "Email",
        "mobile_app_chat": "Mobile App Chat",
        "twilio_sms": "SMS",
        "twilio_whatsapp": "WhatsApp",
    }

    const selectedStatus = searchParams.get("status") || "Open"
    const selectedAssignment = searchParams.get("assignment") || "Assigned To Me"
    const searchQuery = searchParams.get("search") || ""
    const selectedInboxId = searchParams.get("inbox") || null
    const selectedInbox = selectedInboxId ? allInboxes.find(inbox => inbox.id === selectedInboxId) || null : null
    const selectedChannelType = searchParams.get("channel_type") || null

    const updateFilter = (key, value) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            if (value) {
                next.set(key, value)
            } else {
                next.delete(key)
            }
            return next
        }, { replace: true })
    }
    const [showNewModal, setShowNewModal] = useState(false)
    const currentUserPk = currentUser?.pk ?? null

    useEffect(() => {
    // don’t start listening until inboxes are loaded
    if (loadingInboxes) return;

    if (allInboxesIds.length === 0) {
        setConversations([]);
        setLoading(false);
        return;
    }

    setLoading(true);

    let q = query(
        collection(firestoreDb, COMMS_CONVERSATIONS_COLLECTION_NAME),
        where("inbox_graphene_ids", "array-contains-any", allInboxesIds),
        orderBy("last_message_at", "desc"),
        limit(500)
    );

    if (selectedStatus && selectedStatus !== "All") {
        q = query(
        collection(firestoreDb, COMMS_CONVERSATIONS_COLLECTION_NAME),
        where("inbox_graphene_ids", "array-contains-any", allInboxesIds),
        where("status", "==", selectedStatus === "Archived" ? "CLOSED" : selectedStatus.toUpperCase()),
        orderBy("last_message_at", "desc"),
        limit(500)
        );
    }

    const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
        const fetched = snapshot.docs.map((doc) => doc.data());
        setConversations(fetched);
        setLoading(false);
        setError(null);
        },
        (err) => {
        console.error("Error fetching conversations:", err);
        setError("Error fetching conversations");
        setLoading(false);
        }
    );

    // Cleanup subscription on unmount or when dependencies change
    return () => unsubscribe();
    }, [loadingInboxes, allInboxesIdsKey, selectedStatus]);

    // Filter conversations by selected inbox and search query
    const filteredConversations = conversations.filter(conversation => {
        // If inbox is selected, filter by inbox otherwise show all
        let matchesInbox = true
        if (selectedInbox) {
            matchesInbox = conversation.inbox_graphene_ids && conversation.inbox_graphene_ids.includes(selectedInbox.id)
        }
        // Search filter
        const matchesSearch = searchQuery === "" 
            || (conversation.subject && conversation.subject.toLowerCase().includes(searchQuery.toLowerCase()))
            || (conversation.contact_name && conversation.contact_name.toLowerCase().includes(searchQuery.toLowerCase()))
            || (conversation.contact_email && conversation.contact_email.toLowerCase().includes(searchQuery.toLowerCase()))
            || (conversation.contact_phone && conversation.contact_phone.toLowerCase().includes(searchQuery.toLowerCase()))
            || (conversation.driver_name && conversation.driver_name.toLowerCase().includes(searchQuery.toLowerCase()))
        
        // Assignment filter
        const matchesAssignment = selectedAssignment === "All"
            || (selectedAssignment === "Assigned To Me" && conversation.assigned_to === currentUserPk)
            || (selectedAssignment === "Unassigned" && !conversation.assigned_to)
            || (selectedAssignment === "Not Assigned To Me" && conversation.assigned_to && conversation.assigned_to !== currentUserPk)

        // Channel type filter
        const matchesChannelType = !selectedChannelType || conversation.channel_type === selectedChannelType

        return matchesInbox && matchesSearch && matchesAssignment && matchesChannelType
    })

    const formatTimestamp = (isoString) => {
        if (!isoString) return ''
        const m = moment(isoString)
        if (!m.isValid()) return ''
        return m.calendar(undefined, {
            sameDay: 'LT',
            lastDay: '[Yesterday] LT',
            lastWeek: 'MMM D',
            sameElse: 'MMM D'
        })
    }

    const formatStatusLabel = (status) => {
        if (!status) return 'Open'
        if (status.toUpperCase() === 'CLOSED') return 'Archived'
        return status.charAt(0).toUpperCase() + status.slice(1)
    }

    const onConversationArchived = (conversationId) => {
        // Redirect to the next open conversation after conversationId if any, otherwise, stay put
        // Get next item
        const currentIndex = filteredConversations.findIndex(conv => conv.graphene_id === conversationId)
        if (currentIndex === -1) return // if not found, do nothing
        const nextConv = filteredConversations[currentIndex + 1] || filteredConversations[currentIndex - 1]
        if (!nextConv) return // if not found, do nothing
        if (nextConv) {
            navigate({ pathname: `/comms/conversation/${nextConv.graphene_id}/`, search: searchParams.toString() }, { replace: true })
        }
    }
    
    // On first load, if no conversationId in URL and there are conversations, redirect to first conversation
    useEffect(() => {
    if (!conversationId && !loading && !loadingInboxes && filteredConversations.length > 0) {
        navigate({ pathname: `/comms/conversation/${filteredConversations[0].graphene_id}/`, search: searchParams.toString() }, { replace: true })
        }
    }, [conversationId, filteredConversations, loading, loadingInboxes, navigate])

    if (loading) {
        return <Loader />
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>
    }

    if (!allInboxes || allInboxes.length === 0) {
        return <div className="alert alert-warning">No inboxes available.</div>
    }
    return (
        <Container fluid className='CommsContainer'>
            <div className="CommsBottomActions">
                <Link to="#" className="CommsNewConversationButton" title="Start new conversation" onClick={() => setShowNewModal(true)}>
                    <i className="fa fa-plus" aria-hidden="true"></i>
                </Link>
            </div>
            <CommsNewConversationModal
                isOpen={showNewModal}
                toggle={() => setShowNewModal(!showNewModal)}
                allInboxes={allInboxes}
                currentUser={currentUser}
            />
            <Row>
                {/* Left column for conversation list */}
                <Col xs="2" className="CommsColumn">
                    <div className="CommsFilterBar">
                        <div className="InboxDropdown">
                            <Dropdown isOpen={inboxOpen} toggle={() => setInboxOpen(!inboxOpen)}>
                            <DropdownToggle caret>{selectedInbox?.name || "All Inbox"}</DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem header>Choose Inbox</DropdownItem>
                                <DropdownItem onClick={() => updateFilter("inbox", null)}> All </DropdownItem>
                                {allInboxes.map((inbox, idx) => (
                                <DropdownItem
                                    key={idx}
                                    onClick={() => updateFilter("inbox", inbox.id)}
                                >
                                    {inbox.name}
                                </DropdownItem>
                                ))}
                            </DropdownMenu>
                            </Dropdown>
                        </div>
                        <div className="StatusDropdown">
                            <Dropdown isOpen={channelOpen} toggle={() => setChannelOpen(!channelOpen)}>
                            <DropdownToggle caret>{selectedChannelType ? CHANNEL_TYPE_LABELS[selectedChannelType] || selectedChannelType : "All Channels"}</DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem header>Filter by Channel</DropdownItem>
                                <DropdownItem onClick={() => updateFilter("channel_type", null)}>All Channels</DropdownItem>
                                {Object.entries(CHANNEL_TYPE_LABELS).map(([value, label]) => (
                                    <DropdownItem key={value} onClick={() => updateFilter("channel_type", value)}>
                                        {label}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                            </Dropdown>
                        </div>

                        <div className="StatusDropdown">
                            <Dropdown isOpen={assignmentOpen} toggle={() => setAssignmentOpen(!assignmentOpen)}>
                            <DropdownToggle caret>{selectedAssignment}</DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem header>Filter by Assignment</DropdownItem>
                                {ASSIGNMENT_OPTIONS.map((option, idx) => (
                                <DropdownItem
                                    key={idx}
                                    onClick={() => updateFilter("assignment", option)}
                                >
                                    {option}
                                </DropdownItem>
                                ))}
                            </DropdownMenu>
                            </Dropdown>
                        </div>
                        
                        <div className="StatusDropdown">
                            <Dropdown isOpen={statusOpen} toggle={() => setStatusOpen(!statusOpen)}>
                            <DropdownToggle caret>{selectedStatus}</DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem header>Filter by Status</DropdownItem>
                                {STATUSES.map((status, idx) => (
                                <DropdownItem
                                    key={idx}
                                    onClick={() => updateFilter("status", status)}
                                >
                                    {status}
                                </DropdownItem>
                                ))}
                            </DropdownMenu>
                            </Dropdown>
                        </div>
                    </div>
                    
                    {/* Search bar */}
                    <div>
                        <input
                            type="text"
                            placeholder="Search conversations..."
                            value={searchQuery}
                            onChange={e => updateFilter("search", e.target.value)}
                            className="form-control"
                        />
                    </div>
                    <ul className="CommsList">
                        {filteredConversations.length > 0 && filteredConversations.map((conversationItem, idx) => {
                            const isActive = conversationId === conversationItem.graphene_id
                            const isAssignedUnread = currentUserPk && conversationItem.assigned_to === currentUserPk && conversationItem.is_read_by_assigned_agent === false
                            const listItemClass = [isActive ? 'active' : 'inactive', isAssignedUnread ? 'assigned-unread' : ''].filter(Boolean).join(' ')
                            const subject = conversationItem.subject || conversationItem.contact_name || 'Conversation'
                            const contactLine = conversationItem.contact_phone || conversationItem.contact_email
                            const driverLine = conversationItem.driver_name
                            const statusLabel = formatStatusLabel(conversationItem.status)
                            const timestampLabel = formatTimestamp(conversationItem.last_message_at)
                            const statusClass = conversationItem.status ? `status-${conversationItem.status.toLowerCase()}` : 'status-open'

                            return (
                                <li className={listItemClass} key={idx}>
                                    <Link to={{ pathname: `/comms/conversation/${conversationItem.graphene_id}/`, search: searchParams.toString() }} className="CommsListLink">
                                        <div className="CommsListHeader">
                                            <span className="CommsListSubject" title={subject}>{subject}</span>
                                        </div>
                                        {(contactLine || driverLine) && (
                                            <div className="CommsListMeta">
                                                {contactLine && <span>{contactLine}</span>}
                                                {driverLine && <span>{driverLine}</span>}
                                            </div>
                                        )}
                                        <div className="CommsListFooter">
                                            <span className={`CommsListStatus ${statusClass}`}>{statusLabel}</span>
                                            {conversationItem?.assigned_to_username && <span className={`CommsListStatus`}>{conversationItem?.assigned_to_username}</span>}
                                            
                                            {isAssignedUnread && (
                                                    <span className="CommsListUnreadBadge" aria-label="Unread conversation">
                                                        Unread
                                                    </span>
                                                )}
                                            {timestampLabel && <span className="CommsListTime">{timestampLabel}</span>}
                                        </div>
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </Col>

                {/* Right column for displaying the CommsConversation */}
                <Col xs="10">
                    <AllUsersPresenceProvider>
                        <CommsConversation conversationIdFromParent={conversationId} onConversationArchived={onConversationArchived}  />
                    </AllUsersPresenceProvider>
                </Col>
            </Row>
        </Container>
    )
}
